import { useEffect, useMemo, useState } from 'react';
import { GoogleMap, Marker, useJsApiLoader, InfoWindow } from '@react-google-maps/api';
import { RefreshCw, MapPin, Filter, X, Users, ChevronRight } from 'lucide-react';
import {
  contractService,
  erpDataService,
  userLocationService,
} from './_shared/api';
import { maskDateBR } from './_shared/utils/date';
import type {
  ActiveUserLocation,
  ApprovalStatus,
  ContractGeoPoint,
  UserRole,
  Vendedor,
} from './_shared/types';
import ContractDetailModal from './_components/ContractDetailModal';
import { useTheme } from '../../context/ThemeContext';
import { useModalKeys } from '../../hooks/useModalKeys';

/**
 * Prospect > Map — mapa de contratos geocoded + vendedores online.
 *
 * Porte do apps/prospect-web/src/pages/Map.tsx (467 lns) com adaptações:
 * - Imports refatorados pros paths do shell SAAS
 * - InfoWindow do contrato linka pro ContractDetailModal admin-only
 *   (Fase 14d) com Aprovar/Reprovar inline
 *
 * Requer VITE_GOOGLE_MAPS_API_KEY no .env do apps/web.
 */
const DEFAULT_CENTER = { lat: -23.5505, lng: -47.4582 }; // Sorocaba/SP

// Cor dos pinos no mapa. Literal de propósito: precisa de saturação alta
// para sobressair sobre o mapa, e não acompanha o tema do painel.
const STATUS_COLORS: Record<ApprovalStatus, string> = {
  pending: '#fb923c',
  approved: '#10b981',
  rejected: '#ef4444',
  declined: '#eab308',
};

const STATUS_LABEL: Record<ApprovalStatus, string> = {
  pending: 'Pendente',
  approved: 'Aprovado',
  rejected: 'Reprovado',
  declined: 'Declinado',
};

const ROLE_COLOR: Record<UserRole, string> = {
  admin: '#3b82f6',
  supervisor: '#a78bfa',
  monitor: '#f59e0b',
  user: '#94a3b8',
};

interface Filters {
  vendedorId?: number;
  dateFrom: string;
  dateTo: string;
  approvalStatuses: ApprovalStatus[];
}

export default function ProspectMap() {
  const apiKey = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined) ?? '';
  const { isLoaded } = useJsApiLoader({ id: 'google-maps', googleMapsApiKey: apiKey });
  // Estilo do mapa acompanha o tema do painel (claro = estilo default do Google)
  const { resolved } = useTheme();

  const [points, setPoints] = useState<ContractGeoPoint[]>([]);
  const [activeUsers, setActiveUsers] = useState<ActiveUserLocation[]>([]);
  const [vendedores, setVendedores] = useState<Vendedor[]>([]);
  const [selectedContract, setSelectedContract] = useState<ContractGeoPoint | null>(null);
  const [selectedUser, setSelectedUser] = useState<ActiveUserLocation | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailContractId, setDetailContractId] = useState<string | null>(null);

  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState<Filters>({
    vendedorId: undefined,
    dateFrom: '',
    dateTo: '',
    approvalStatuses: [],
  });

  async function load(): Promise<void> {
    setLoading(true);
    try {
      const [geo, users] = await Promise.all([
        contractService.getGeo({
          vendedorId: filters.vendedorId,
          dateFrom: filters.dateFrom || undefined,
          dateTo: filters.dateTo || undefined,
          approvalStatuses: filters.approvalStatuses.length
            ? filters.approvalStatuses
            : undefined,
        }),
        userLocationService.listActive().catch(() => [] as ActiveUserLocation[]),
      ]);
      setPoints(geo);
      setActiveUsers(users);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    erpDataService.getVendedores().then(setVendedores).catch(() => undefined);
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      userLocationService.listActive().then(setActiveUsers).catch(() => undefined);
    }, 60_000);
    return () => clearInterval(id);
  }, []);

  const center = useMemo(() => {
    if (points.length > 0 && points[0]) return { lat: points[0].lat, lng: points[0].lng };
    if (activeUsers.length > 0 && activeUsers[0]) return { lat: activeUsers[0].lat, lng: activeUsers[0].lng };
    return DEFAULT_CENTER;
  }, [points, activeUsers]);

  const statusCount = useMemo(() => {
    const c = { pending: 0, approved: 0, rejected: 0, declined: 0 };
    points.forEach((p) => {
      c[p.approvalStatus] = (c[p.approvalStatus] ?? 0) + 1;
    });
    return c;
  }, [points]);

  const activeFilters =
    (filters.vendedorId ? 1 : 0) +
    (filters.dateFrom ? 1 : 0) +
    (filters.dateTo ? 1 : 0) +
    (filters.approvalStatuses.length > 0 ? 1 : 0);

  if (!apiKey) {
    return (
      <div className="p-6">
        <div className="bg-warning-soft/30 border border-warning-line/40 rounded-2xl p-4">
          <p className="text-warning text-sm">
            Configure <code className="bg-hover px-1.5 py-0.5 rounded">VITE_GOOGLE_MAPS_API_KEY</code>{' '}
            no <code className="bg-hover px-1.5 py-0.5 rounded">.env</code> do
            apps/web e reinicie o dev server.
          </p>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="h-[calc(100vh-4rem)] flex items-center justify-center">
        <RefreshCw size={28} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="relative h-[calc(100vh-4rem)]">
      {/* Header flutuante */}
      <div className="absolute top-4 left-4 right-4 z-10 flex items-center justify-between gap-2 pointer-events-none">
        <div className="bg-surface/95 backdrop-blur border border-line rounded-xl px-3 py-2 pointer-events-auto flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5 text-sm">
            <MapPin size={14} className="text-primary" />
            <span className="text-fg font-bold">{points.length}</span>
            <span className="text-fg-2 text-xs">contratos</span>
          </div>
          <div className="h-4 w-px bg-hover" />
          <div className="flex items-center gap-1.5 text-sm">
            <Users size={14} className="text-success" />
            <span className="text-fg font-bold">{activeUsers.length}</span>
            <span className="text-fg-2 text-xs">online</span>
          </div>
        </div>

        <div className="flex items-center gap-2 pointer-events-auto">
          <button
            onClick={() => setFilterOpen(true)}
            className="relative w-10 h-10 rounded-full bg-surface/95 backdrop-blur border border-line hover:bg-canvas flex items-center justify-center transition"
          >
            <Filter
              size={18}
              className={activeFilters > 0 ? 'text-primary' : 'text-fg'}
            />
            {activeFilters > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-primary text-white text-[10px] font-bold flex items-center justify-center border-2 border-surface">
                {activeFilters}
              </span>
            )}
          </button>
          <button
            onClick={() => void load()}
            disabled={loading}
            className="w-10 h-10 rounded-full bg-surface/95 backdrop-blur border border-line hover:bg-canvas flex items-center justify-center transition"
          >
            <RefreshCw
              size={18}
              className={loading ? 'animate-spin text-primary' : 'text-fg'}
            />
          </button>
        </div>
      </div>

      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '100%' }}
        center={center}
        zoom={12}
        options={{
          disableDefaultUI: true,
          zoomControl: true,
          styles: resolved === 'dark' ? DARK_MAP_STYLE : undefined,
        }}
      >
        {/* Pins de contratos */}
        {points.map((p) => (
          <Marker
            key={p.id}
            position={{ lat: p.lat, lng: p.lng }}
            onClick={() => {
              setSelectedContract(p);
              setSelectedUser(null);
            }}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              fillColor: STATUS_COLORS[p.approvalStatus] ?? '#94a3b8',
              fillOpacity: 1,
              strokeColor: '#fff',
              strokeWeight: 2,
              scale: 9,
            }}
          />
        ))}

        {/* Pins de usuários online */}
        {activeUsers.map((u) => (
          <Marker
            key={u.userId}
            position={{ lat: u.lat, lng: u.lng }}
            onClick={() => {
              setSelectedUser(u);
              setSelectedContract(null);
            }}
            icon={{
              path: 'M12,2C8.13,2 5,5.13 5,9c0,5.25 7,13 7,13s7,-7.75 7,-13c0,-3.87 -3.13,-7 -7,-7zM12,11.5c-1.38,0 -2.5,-1.12 -2.5,-2.5s1.12,-2.5 2.5,-2.5 2.5,1.12 2.5,2.5 -1.12,2.5 -2.5,2.5z',
              fillColor: ROLE_COLOR[u.role],
              fillOpacity: 1,
              strokeColor: '#fff',
              strokeWeight: 1.5,
              scale: 1.6,
              anchor: new window.google.maps.Point(12, 22),
            }}
            zIndex={1000}
          />
        ))}

        {/* InfoWindow contrato.
            O balão é renderizado pelo Google sobre fundo branco fixo, fora
            do nosso tema — por isso o texto aqui usa slate literal em vez
            de token: com `text-fg` sumiria no tema escuro. */}
        {selectedContract && (
          <InfoWindow
            position={{ lat: selectedContract.lat, lng: selectedContract.lng }}
            onCloseClick={() => setSelectedContract(null)}
          >
            <div className="p-1 min-w-[240px] text-slate-900">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span
                  className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase text-white"
                  style={{
                    backgroundColor:
                      STATUS_COLORS[selectedContract.approvalStatus] ?? '#94a3b8',
                  }}
                >
                  {STATUS_LABEL[selectedContract.approvalStatus]}
                </span>
                {selectedContract.erpContratoId && (
                  <span className="text-xs font-bold text-primary-solid">
                    #{selectedContract.erpContratoId}
                  </span>
                )}
              </div>
              <p className="font-bold text-sm">{selectedContract.clienteNome}</p>
              {selectedContract.plano && (
                <p className="text-xs text-slate-700 mt-1">{selectedContract.plano}</p>
              )}
              <p className="text-xs text-faint mt-1">
                {[selectedContract.logradouro, selectedContract.numero, selectedContract.bairro]
                  .filter(Boolean)
                  .join(', ')}
              </p>
              {selectedContract.vendedorNome && (
                <p className="text-xs text-faint mt-1.5">
                  <strong>Vendedor:</strong> {selectedContract.vendedorNome}
                </p>
              )}
              <p className="text-xs text-muted mt-1">
                {new Date(selectedContract.createdAt).toLocaleString('pt-BR')}
              </p>
              <button
                onClick={() => {
                  setDetailContractId(selectedContract.id);
                  setSelectedContract(null);
                }}
                className="mt-2 w-full bg-primary-solid hover:bg-primary-solid-hover rounded-lg py-1.5 text-white text-xs font-bold flex items-center justify-center gap-1"
              >
                Ver detalhes <ChevronRight size={12} />
              </button>
            </div>
          </InfoWindow>
        )}

        {/* InfoWindow usuário ativo */}
        {selectedUser && (
          <InfoWindow
            position={{ lat: selectedUser.lat, lng: selectedUser.lng }}
            onCloseClick={() => setSelectedUser(null)}
          >
            <div className="p-1 min-w-[200px] text-slate-900">
              <div className="flex items-center gap-2 mb-2">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs"
                  style={{ backgroundColor: ROLE_COLOR[selectedUser.role] }}
                >
                  {selectedUser.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="font-bold text-sm leading-tight">{selectedUser.name}</p>
                  <p
                    className="text-[10px] uppercase tracking-wide"
                    style={{ color: ROLE_COLOR[selectedUser.role] }}
                  >
                    {selectedUser.role}
                  </p>
                </div>
              </div>
              <p className="text-xs text-faint">{selectedUser.email}</p>
              <p className="text-xs text-muted mt-1">
                Atualizado {timeAgo(selectedUser.updatedAt)}
              </p>
              {selectedUser.accuracy && (
                <p className="text-[10px] text-muted">
                  Precisão: ±{Math.round(selectedUser.accuracy)}m
                </p>
              )}
            </div>
          </InfoWindow>
        )}
      </GoogleMap>

      {/* Legenda */}
      <div className="absolute bottom-4 left-4 bg-surface/95 backdrop-blur border border-line rounded-xl px-3 py-2.5 z-10">
        <p className="text-[10px] uppercase font-bold text-fg-2 mb-2">Contratos</p>
        <div className="flex flex-col gap-1 text-xs mb-3">
          <Legend color={STATUS_COLORS.approved} label={`Aprovado (${statusCount.approved})`} />
          <Legend color={STATUS_COLORS.pending} label={`Pendente (${statusCount.pending})`} />
          <Legend color={STATUS_COLORS.rejected} label={`Reprovado (${statusCount.rejected})`} />
          <Legend color={STATUS_COLORS.declined} label={`Declinado (${statusCount.declined})`} />
        </div>
        {activeUsers.length > 0 && (
          <>
            <p className="text-[10px] uppercase font-bold text-fg-2 mb-2">Online</p>
            <div className="flex flex-col gap-1 text-xs">
              <Legend color={ROLE_COLOR.admin} label="Admin" />
              <Legend color={ROLE_COLOR.supervisor} label="Supervisor" />
              <Legend color={ROLE_COLOR.user} label="Vendedor" />
            </div>
          </>
        )}
      </div>

      {/* Filtros */}
      {filterOpen && (
        <FilterModal
          filters={filters}
          vendedores={vendedores}
          onApply={(f) => {
            setFilters(f);
            setFilterOpen(false);
          }}
          onClose={() => setFilterOpen(false)}
        />
      )}

      {/* Detail */}
      <ContractDetailModal
        contractId={detailContractId}
        onClose={() => setDetailContractId(null)}
        onChanged={() => void load()}
      />
    </div>
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
      <span className="text-fg">{label}</span>
    </div>
  );
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'agora';
  if (m < 60) return `há ${m} min`;
  const h = Math.floor(m / 60);
  return `há ${h}h`;
}

function FilterModal({
  filters,
  vendedores,
  onApply,
  onClose,
}: {
  filters: Filters;
  vendedores: Vendedor[];
  onApply: (f: Filters) => void;
  onClose: () => void;
}) {
  const [local, setLocal] = useState<Filters>(filters);

  function toggleStatus(s: ApprovalStatus): void {
    setLocal((p) => ({
      ...p,
      approvalStatuses: p.approvalStatuses.includes(s)
        ? p.approvalStatuses.filter((x) => x !== s)
        : [...p.approvalStatuses, s],
    }));
  }

  // Esc fecha; Enter aplica os filtros; clique fora NÃO fecha (Fase 30)
  useModalKeys(onClose, () => onApply(local));

  return (
    <div className="fixed inset-0 z-30 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-surface rounded-2xl border border-line max-w-md w-full p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-fg">Filtros do mapa</h2>
          <button onClick={onClose} className="text-fg-2 hover:text-fg">
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-xs uppercase text-fg-2 mb-1.5 block">Vendedor</label>
            <select
              value={local.vendedorId ?? ''}
              onChange={(e) =>
                setLocal({
                  ...local,
                  vendedorId: e.target.value ? Number(e.target.value) : undefined,
                })
              }
              className="w-full bg-canvas border border-line rounded-xl h-11 px-3 text-fg outline-none focus:border-primary"
            >
              <option value="">Todos os vendedores</option>
              {vendedores.map((v) => (
                <option key={v.id} value={v.erpId}>
                  {v.nome}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs uppercase text-fg-2 mb-1.5 block">Período</label>
            <div className="flex gap-2">
              <input
                placeholder="De DD/MM/AAAA"
                value={local.dateFrom}
                onChange={(e) => setLocal({ ...local, dateFrom: maskDateBR(e.target.value) })}
                className="flex-1 bg-canvas border border-line rounded-xl h-11 px-3 text-fg outline-none focus:border-primary placeholder:text-muted"
              />
              <input
                placeholder="Até DD/MM/AAAA"
                value={local.dateTo}
                onChange={(e) => setLocal({ ...local, dateTo: maskDateBR(e.target.value) })}
                className="flex-1 bg-canvas border border-line rounded-xl h-11 px-3 text-fg outline-none focus:border-primary placeholder:text-muted"
              />
            </div>
          </div>

          <div>
            <label className="text-xs uppercase text-fg-2 mb-1.5 block">Status</label>
            <div className="flex gap-2">
              {(['pending', 'approved', 'rejected', 'declined'] as ApprovalStatus[]).map((s) => {
                const active = local.approvalStatuses.includes(s);
                return (
                  <button
                    key={s}
                    onClick={() => toggleStatus(s)}
                    className={`flex-1 h-10 rounded-xl text-xs font-bold border transition ${
                      active
                        ? 'text-white border-transparent'
                        : 'bg-canvas border-line text-fg-2 hover:bg-hover'
                    }`}
                    style={active ? { backgroundColor: STATUS_COLORS[s] } : {}}
                  >
                    {STATUS_LABEL[s]}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex gap-2 mt-5">
          <button
            onClick={() =>
              onApply({
                vendedorId: undefined,
                dateFrom: '',
                dateTo: '',
                approvalStatuses: [],
              })
            }
            className="flex-1 h-11 bg-canvas hover:bg-hover rounded-xl text-fg-2 font-medium"
          >
            Limpar
          </button>
          <button
            onClick={() => onApply(local)}
            className="flex-1 h-11 bg-primary-solid hover:bg-primary-solid-hover rounded-xl text-white font-bold"
          >
            Aplicar
          </button>
        </div>
      </div>
    </div>
  );
}

// Estilo do Google Maps — a API só aceita hex literal, então não dá pra usar
// token. Aplicado só no tema escuro (no claro vale o estilo padrão do Google).
const DARK_MAP_STYLE: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#1e293b' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#1e293b' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#94a3b8' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#334155' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0f172a' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
];
